import { Dispatch, SetStateAction } from "react";

import {
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import { ILead } from "../../models/Lead";
import classNames from "../../utils/classNames";
import Tooltip from "../shared/Tooltip";
import UnbookedEvents from "./UnbookedEvents";

const sortingOptions = [
  "Postcode A-Z",
  "VRM A-Z",
  "Oldest to Newest",
  "Newest to Oldest",
];

type UnscheduledLeadsPanelProps = {
  isTop: boolean;
  showUnscheduledAtTop: boolean;
  unbooked: ILead[];
  selected?: ILead;
  handleSelected?: any;
  year: number;
  week: number;
  currentFilter: string;
  setCurrentFilter: Dispatch<SetStateAction<string>>;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  showCollected: boolean;
  setShowCollected: Dispatch<SetStateAction<boolean>>;
  showNotPaid: boolean;
  setShowNotPaid: Dispatch<SetStateAction<boolean>>;
  setShowUnscheduledAtTop: Dispatch<SetStateAction<boolean>>;
};

export default function UnscheduledLeadsPanel({
  isTop,
  showUnscheduledAtTop,
  unbooked,
  selected,
  handleSelected,
  year,
  week,
  currentFilter,
  setCurrentFilter,
  setSearchTerm,
  showCollected,
  setShowCollected,
  showNotPaid,
  setShowNotPaid,
  setShowUnscheduledAtTop,
}: UnscheduledLeadsPanelProps) {
  const clearSelectedLead = () => {
    handleSelected?.(undefined);
  };

  return (
    <div className="border border-gray-300 rounded-md bg-white">
      <div
        className={classNames(
          isTop ? "flex flex-wrap items-end gap-3" : "",
          "border-b border-gray-300 p-3"
        )}
      >
        <div className={isTop ? "w-56" : ""}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-700">
              Filter & Sort
            </p>
            <Tooltip
              content={
                showUnscheduledAtTop
                  ? "Show unscheduled leads on the side"
                  : "Show unscheduled leads at the top"
              }
            >
              <button
                type="button"
                onClick={() => setShowUnscheduledAtTop(!showUnscheduledAtTop)}
                className="text-primary-600 hover:text-primary-700 disabled:text-gray-500 disabled:hover:text-gray-500"
              >
                <ArrowsRightLeftIcon className="h-5 w-5" />
                <span className="sr-only">
                  Toggle unscheduled leads position
                </span>
              </button>
            </Tooltip>
          </div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <MagnifyingGlassIcon
                className="h-4 w-4 text-gray-400"
                aria-hidden="true"
              />
            </div>
            <input
              type="text"
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-7 text-xs"
              placeholder="VRM, Postcode or Name"
            />
          </div>
        </div>
        <select
          aria-label="Unscheduled Leads Sorter"
          onChange={(e) => setCurrentFilter(e.target.value)}
          defaultValue={currentFilter}
          className={classNames(isTop ? "w-48" : "w-full mt-2", "text-xs")}
        >
          {sortingOptions.map((filter, i) => (
            <option className="text-xs" key={`${filter}-${i}`}>
              {String(filter)}
            </option>
          ))}
        </select>
        <div
          className={classNames(
            isTop ? "flex gap-x-4 pb-2" : "flex flex-col gap-y-1 mt-2",
            "text-xs text-gray-700"
          )}
        >
          <label className="flex items-center gap-x-2">
            <input
              type="checkbox"
              checked={showCollected}
              onChange={() => {
                clearSelectedLead();
                setShowCollected(!showCollected);
              }}
            />
            Collected
          </label>
          <label className="flex items-center gap-x-2">
            <input
              type="checkbox"
              checked={showNotPaid}
              onChange={() => {
                clearSelectedLead();
                setShowNotPaid(!showNotPaid);
              }}
            />
            Not Paid
          </label>
        </div>
      </div>

      <div className="p-3 border-b border-gray-300">
        <div className="flex justify-between items-baseline">
          <p className="text-sm font-semibold text-gray-700">
            Unscheduled Leads
          </p>
          <span className="text-xs text-gray-500">{unbooked.length}</span>
        </div>
      </div>

      <div
        className={classNames(
          isTop ? "max-h-[18vh]" : "max-h-[80vh]",
          "p-1 overflow-y-auto"
        )}
      >
        <UnbookedEvents
          unbookedLeads={unbooked}
          selected={selected}
          handleSelected={handleSelected}
          year={year}
          week={week}
          vertical={!isTop}
        />
      </div>
    </div>
  );
}
